/**
 * telegram.store.ts — Pinia-стор Telegram-провайдера. DEV-878 / ADR-0030.
 * По образцу zalo.store.ts: сессия + QR-логин-флоу, чаты, открытые чаты
 * (сегменты), сообщения (общие ChatInfo/MessageInfo), оптимистичная отправка
 * и мутации под WS-события. peer_key — непрозрачный идентификатор пира.
 *
 * DEV-1835: сообщения лежат в сегментах `Map<chatKey, ChatSegment>` — по одному
 * на пару (источник, пир). В обычном режиме открыт один сегмент, и v1-фасад
 * (currentPeerKey/messages/currentChatInfo) смотрит на него.
 */
import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { telegramApi } from '@/features/messenger/api/telegram.api'
import {
  deliveryIndex,
  findDeliveredUnsent,
  isLocalMessage,
  isOwnOutgoing,
  newPendingId,
  toFailedId,
} from '@/features/messenger/utils/localMessage'
import { useAuthStore } from '@/features/auth/stores/auth.store'
import {
  createTelegramSession,
  reportTelegramError,
} from '@/features/messenger/stores/telegramSession'
import { BOT_AUTHOR_TYPE } from '@/features/messenger/types/messenger.types'
import type {
  ChatInfo,
  MessageInfo,
  MediaType,
  MessageStatus,
} from '@/features/messenger/types/messenger.types'
import {
  SUPPORT_MANAGER_ID,
  OWN_SOURCE_ID,
  buildChatKey,
  buildMessageUid,
} from '@/features/messenger/types/telegram.types'
import type {
  ChatSegment,
  TelegramFeedMessage,
  TelegramChatInfo,
  TelegramMessageInfo,
  TelegramSendTextRequest,
  TelegramSendImageRequest,
  TelegramSendDocumentRequest,
  TelegramSendMessageResponse,
  TelegramMarkAsReadRequest,
  TelegramReactRequest,
  WsTelegramNewMessagePayload,
  WsTelegramMessageSentPayload,
  WsTelegramMessageStatusPayload,
  WsTelegramMediaUpdatePayload,
  WsTelegramReactionPayload,
  WsTelegramEditedPayload,
  WsTelegramDeletedPayload,
  WsTelegramTypingPayload,
  WsTelegramAccountStatusPayload,
  WsTelegramAccountSnapshot,
  TelegramAccountStatus,
  TelegramSendFailure,
} from '@/features/messenger/types/telegram.types'

// Коды gRPC в теле ответа gateway (DefaultHTTPErrorHandler): {"code":N,"message":"…"}.
const GRPC_RESOURCE_EXHAUSTED = 8
const GRPC_FAILED_PRECONDITION = 9

// ===================== Mappers: Telegram → shared types =====================

export function mapTelegramChatToChatInfo(chat: TelegramChatInfo): ChatInfo {
  return {
    jid: chat.peerKey,
    name: chat.title || chat.username || chat.phone || chat.peerKey,
    lastMessageTime: chat.lastMessageTime,
    unreadCount: chat.unreadCount,
    createdAt: chat.createdAt,
    updatedAt: chat.updatedAt,
  }
}

export function mapTelegramMediaType(mediaType: string): MediaType {
  switch (mediaType) {
    case 'text':
      return 'text'
    case 'image':
    case 'photo':
      return 'image'
    case 'document':
    case 'file':
      return 'document'
    case 'audio':
    case 'voice':
      return 'audio'
    case 'video':
      return 'video'
    case 'sticker':
      return 'sticker'
    case 'call':
      return 'call'
    default:
      return 'text'
  }
}

export function mapTelegramMessageToMessageInfo(msg: TelegramMessageInfo): MessageInfo {
  const attachment = msg.attachments?.[0]
  return {
    id: msg.id,
    chatJid: msg.peerKey,
    senderJid: msg.senderTgId,
    content: msg.content,
    timestamp: msg.timestamp,
    isFromMe: msg.isFromMe,
    mediaType: mapTelegramMediaType(msg.mediaType),
    status: (msg.status || 'sent') as MessageStatus,
    isReadByManager: msg.isReadByManager,
    url: attachment?.fileUrl,
    filename: attachment?.fileName,
    fileLength: attachment?.fileSize,
    authorType: msg.authorType,
    authorManagerId: msg.authorManagerId,
    reactions: msg.reactions?.map((r) => ({
      emoji: r.emoji,
      senderJid: r.senderTgId,
      isFromMe: r.isFromMe,
      timestamp: msg.timestamp,
    })),
    createdAt: msg.createdAt,
    updatedAt: msg.updatedAt,
  }
}

// ===================== Store =====================

export const useTelegramStore = defineStore('telegram', () => {
  // ---- account / session + QR login flow (DEV-1712: общая фабрика) ----
  // Сессия вынесена в createTelegramSession и делится с общим аккаунтом
  // поддержки. Здесь она разворачивается в те же поля, что были раньше.
  const session = createTelegramSession({
    scope: 'personal',
    api: {
      getStatus: telegramApi.getStatus,
      startLogin: telegramApi.startLogin,
      submitPassword: ({ loginId, password }) =>
        telegramApi.submitPassword({ login_id: loginId, password }),
      logout: telegramApi.logout,
    },
    // Выход из личного аккаунта гасит и его чаты.
    onLoggedOut: () => {
      chats.value = []
      resetChat()
    },
  })

  const {
    status,
    account,
    isLoadingStatus,
    isStatusKnown,
    isConnected,
    loginState,
    loginId,
    qrText,
    qrImageBase64,
    qrExpiresAt,
    loginError,
    isSubmittingPassword,
    canResumePasswordEntry,
    error,
    loadStatus,
    startLogin,
    submitPassword,
    resumePasswordEntry,
    resetLogin,
    logout,
    applyAccountStatus,
    applyAccountSnapshot,
    handleQrUpdate,
    handleLoginStatus,
  } = session

  // DEV-2741: звонки Telegram — только в CRM. Флаг есть только у личного
  // аккаунта, поэтому он здесь, а не в общей с поддержкой сессии.
  const isSavingCallsCrmOnly = ref(false)
  /**
   * Что просили в упавшем запросе. Включение сервер при отказе запоминает, и
   * переключатель уже стоит на «вкл» — повторить можно только этим значением.
   */
  const failedCallsCrmOnly = ref<boolean | null>(null)

  /** true — звонки переключены во всех других сессиях учётки. */
  async function setCallsCrmOnly(enabled: boolean): Promise<boolean> {
    isSavingCallsCrmOnly.value = true
    failedCallsCrmOnly.value = null
    error.value = null
    try {
      const resp = await telegramApi.setCallsCrmOnly(enabled)
      if (account.value)
        account.value = { ...account.value, callsCrmOnly: resp.callsCrmOnly ?? false }
      return true
    } catch (e) {
      error.value = reportTelegramError('setCallsCrmOnly', e, 'calls_crm_only_failed')
      failedCallsCrmOnly.value = enabled
      // Переключатель показывает сохранённое сервером, а не нажатое; заодно
      // видно, если отказ был из-за отозванной сессии.
      try {
        const resp = await telegramApi.getStatus()
        account.value = resp
        applyAccountStatus(resp.status)
      } catch (statusErr) {
        console.error('[telegram] getStatus after setCallsCrmOnly failed', statusErr)
      }
      return false
    } finally {
      isSavingCallsCrmOnly.value = false
    }
  }

  // ---- chats ----
  const chats = ref<TelegramChatInfo[]>([])
  const chatsTotal = ref(0)
  const isLoadingChats = ref(false)
  // DEV-998: чаты клиента по всем менеджерам (наполняет селектор кросс-просмотра).
  const clientChats = ref<TelegramChatInfo[]>([])

  // ---- open chats / messages (DEV-1835: сегменты) ----
  const segments = ref(new Map<string, ChatSegment>())
  // Объединённая лента: support + личные чаты менеджеров клиента в одном списке.
  const isMergedView = ref(false)
  // Первая загрузка всех сегментов завершена — до неё лента пустая, чтобы одна
  // рано пришедшая история не мелькала без остальных.
  const mergedReady = ref(false)
  // Поколение открытия чата: поздний ответ прежнего open* не трогает новое состояние.
  let openGeneration = 0
  // Пир и владелец «текущего» чата: в обычном режиме — единственный сегмент,
  // в объединённом — support-сегмент (в него уходит отправка).
  const currentPeerKey = ref<string | null>(null)
  // DEV-998/DEV-1833: владелец открытого чата — uuid чужого менеджера или
  // 'support'; null = собственный чат. Чтения несут его как target_manager_id;
  // запись разрешена только для support, WS маршрутизируется по account_id ниже.
  const viewingManagerId = ref<string | null>(null)
  const typingPeerKey = ref<string | null>(null)
  let typingTimer: ReturnType<typeof setTimeout> | null = null

  const authStore = useAuthStore()

  // Источник во фронтовой модели: свой uuid (или пусто — старый бэк) → 'me';
  // 'support' и чужие uuid — как есть. Применяется к REST manager_id и WS account_id.
  function toSourceManagerId(managerId?: string | null): string {
    if (!managerId) return OWN_SOURCE_ID
    // Инвариант: карточка клиента открывается после /me, user уже загружен. Без него
    // свой uuid не свернуть в 'me' — он останется «чужим», в 'me' не сворачиваем вслепую.
    return managerId === authStore.user?.id ? OWN_SOURCE_ID : managerId
  }

  // Обратно: источник → target_manager_id запроса ('me' — без target).
  function toTargetManagerId(sourceManagerId: string): string | undefined {
    return sourceManagerId === OWN_SOURCE_ID ? undefined : sourceManagerId
  }

  const currentSourceId = computed(() => toSourceManagerId(viewingManagerId.value))
  const currentSegment = computed(() =>
    currentPeerKey.value
      ? (segments.value.get(buildChatKey(currentSourceId.value, currentPeerKey.value)) ?? null)
      : null,
  )

  // Лента: в обычном режиме — текущий сегмент, в объединённом — слияние.
  const messages = computed<MessageInfo[]>(() => {
    if (!isMergedView.value) return currentSegment.value?.messages ?? []
    return mergedReady.value ? mergedMessages.value : []
  })
  const hasMoreMessages = computed(() => {
    if (!isMergedView.value) return currentSegment.value?.hasMore ?? false
    for (const segment of segments.value.values()) if (segment.hasMore) return true
    return false
  })
  const currentChatInfo = computed(() => currentSegment.value?.chatInfo ?? null)
  const isLoadingMessages = computed(() => {
    if (isMergedView.value && !mergedReady.value) return true
    for (const segment of segments.value.values()) if (segment.isLoading) return true
    return false
  })

  // ---- слияние (DEV-1835) ----

  // pending-… не число — такие сообщения новее любых серверных.
  function messageOrdinal(id: string): number {
    const ordinal = Number(id)
    return Number.isFinite(ordinal) ? ordinal : Number.POSITIVE_INFINITY
  }

  // Время у бэка секундное — при равенстве порядок держат источник и tg_msg_id.
  function compareFeedMessages(a: TelegramFeedMessage, b: TelegramFeedMessage): number {
    const byTime = a.timestampMs - b.timestampMs
    if (byTime) return byTime
    const bySource = a.sourceManagerId.localeCompare(b.sourceManagerId)
    if (bySource) return bySource
    return messageOrdinal(a.id) - messageOrdinal(b.id)
  }

  function oldestLoadedTime(segment: ChatSegment): number | null {
    const oldest = segment.messages[0]
    return oldest ? oldest.timestampMs : null
  }

  // Граница отображения: самая поздняя из «самых старых загруженных» по сегментам,
  // у которых история ещё не дочитана. Старше неё у такого сегмента могут быть
  // ещё не загруженные сообщения — показывать чужие там значит показывать дыру.
  const mergedBoundary = computed<number | null>(() => {
    let boundary: number | null = null
    for (const segment of segments.value.values()) {
      if (!segment.hasMore) continue
      const oldest = oldestLoadedTime(segment)
      if (oldest === null) continue
      if (boundary === null || oldest > boundary) boundary = oldest
    }
    return boundary
  })

  const mergedMessages = computed<MessageInfo[]>(() => {
    const all: TelegramFeedMessage[] = []
    for (const segment of segments.value.values()) all.push(...segment.messages)
    all.sort(compareFeedMessages)
    const boundary = mergedBoundary.value
    if (boundary === null) return all
    return all.filter((m) => m.timestampMs >= boundary)
  })

  // DEV-2227: источники, у которых всё загруженное старше границы, — в ленте их не видно
  // совсем (свой чат активный, поддержка короткая и старая: «поддержка не общалась»).
  const hiddenMergedSources = computed<string[]>(() => {
    const boundary = mergedBoundary.value
    if (!isMergedView.value || boundary === null) return []
    const hidden: string[] = []
    for (const segment of segments.value.values()) {
      const newest = segment.messages[segment.messages.length - 1]
      if (newest && newest.timestampMs < boundary) hidden.push(segment.sourceManagerId)
    }
    return hidden
  })

  // Что грузить дальше в объединённой ленте. Сначала опустошённый (DELETED вычистил
  // всё загруженное) сегмент с недочитанной историей — границу он не держит, но без
  // него hasMoreMessages врёт; иначе — сегмент на границе, его страница сдвинет её.
  function mergedSegmentToLoad(): { segment: ChatSegment; mode: 'first' | 'next' } | null {
    for (const segment of segments.value.values()) {
      if (segment.hasMore && !segment.isLoading && segment.messages.length === 0) {
        return { segment, mode: 'first' }
      }
    }
    const boundary = mergedBoundary.value
    if (boundary === null) return null
    for (const segment of segments.value.values()) {
      if (segment.hasMore && !segment.isLoading && oldestLoadedTime(segment) === boundary) {
        return { segment, mode: 'next' }
      }
    }
    return null
  }

  // Свой аккаунт и поддержка: отсюда и пишем, и помечаем прочитанным. Чужой менеджер —
  // только просмотр (DEV-998). Права здесь не проверяем, их спрашивает бэкенд: отправка
  // от поддержки требует communications:write_all, прочтение — read_all (DEV-2148).
  function isWritableSource(sourceManagerId: string): boolean {
    return sourceManagerId === OWN_SOURCE_ID || sourceManagerId === SUPPORT_MANAGER_ID
  }

  // Источники открытых сегментов, куда уходит markAsRead: по ним ChatWindow считает
  // «Новые сообщения» в объединённой ленте (чужие менеджерские мы не помечаем).
  const readReceiptSources = computed<string[]>(() => {
    const sources: string[] = []
    for (const segment of segments.value.values()) {
      if (isWritableSource(segment.sourceManagerId)) sources.push(segment.sourceManagerId)
    }
    return sources
  })

  // ===================== chats =====================

  async function loadChats(params?: {
    search?: string
    offset?: number
    limit?: number
    managerId?: string
    clientId?: string
  }) {
    isLoadingChats.value = true
    error.value = null
    try {
      const response = await telegramApi.getChats({
        limit: params?.limit ?? 50,
        offset: params?.offset ?? 0,
        search: params?.search,
        managerId: params?.managerId,
        clientId: params?.clientId,
      })
      chats.value = response.data ?? []
      chatsTotal.value = Number(response.pagination?.total) || 0
    } catch (e) {
      error.value = reportTelegramError('getChats', e)
      // DEV-2227: прежний список — чаты прошлого поиска (и прошлой карточки): по нему
      // findClientChat открыл бы чужой чат.
      chats.value = []
      chatsTotal.value = 0
    } finally {
      isLoadingChats.value = false
    }
  }

  // DEV-998: загрузка чатов клиента для селектора кросс-просмотра: с read_all — по всем
  // менеджерам, без него — поддержка и свои (DEV-2782).
  // false — запрос упал (DEV-2109): карточка показывает ошибку и «повторить», а не
  // молча теряет селектор.
  // keepOnError (DEV-2227): фоновое перечитывание при сбое список не стирает — без него
  // пропадает переключатель, а без личной сессии и вся вкладка.
  async function loadClientChats(
    clientId: string,
    options: { keepOnError?: boolean } = {},
  ): Promise<boolean> {
    error.value = null
    try {
      const resp = await telegramApi.getClientChats(clientId)
      clientChats.value = resp.data ?? []
      return true
    } catch (e) {
      error.value = reportTelegramError('getClientChats', e)
      if (!options.keepOnError) clientChats.value = []
      return false
    }
  }

  // ===================== segments =====================

  function openSegment(peerKey: string, sourceManagerId: string): ChatSegment {
    const chatKey = buildChatKey(sourceManagerId, peerKey)
    segments.value.set(chatKey, {
      chatKey,
      peerKey,
      sourceManagerId,
      messages: [],
      chatInfo: null,
      total: 0,
      offset: 0,
      hasMore: false,
      isLoading: false,
    })
    // Возвращаем реактивный прокси из Map — мутации через него отслеживаются.
    return segments.value.get(chatKey)!
  }

  // Событие/сообщение относится к сегменту (account_id ?? myId, peer_key).
  function segmentForWs(peerKey: string, accountId?: string): ChatSegment | undefined {
    return segments.value.get(buildChatKey(toSourceManagerId(accountId), peerKey))
  }

  // Миллисекунды времени считаем один раз: слияние сравнивает их на каждом пересчёте.
  function toFeedMessage(segment: ChatSegment, message: MessageInfo): TelegramFeedMessage {
    return {
      ...message,
      sourceManagerId: segment.sourceManagerId,
      uid: buildMessageUid(segment.chatKey, message.id),
      timestampMs: Date.parse(message.timestamp),
    }
  }

  // ===================== messages =====================

  const MESSAGES_PAGE_SIZE = 50

  // Возвращает false при ошибке запроса; hasMore при этом не трогает —
  // следующий loadMore повторит попытку (как в v1).
  async function loadSegmentPage(segment: ChatSegment, mode: 'first' | 'next'): Promise<boolean> {
    segment.isLoading = true
    error.value = null
    const offset = mode === 'next' ? segment.offset : 0
    try {
      const response = await telegramApi.getMessages(segment.peerKey, {
        limit: MESSAGES_PAGE_SIZE,
        offset,
        // DEV-998: владелец чужого чата — во ВСЕ его чтения (пагинация, досинхрон
        // после реконнекта WS), иначе бэк вернёт данные своей сессии.
        managerId: toTargetManagerId(segment.sourceManagerId),
      })
      const rows = response.data ?? []
      const page: TelegramFeedMessage[] = []
      let foreignRows = 0
      // Без загруженного user свой uuid не свернуть в 'me' — сверку не делаем.
      const canCheckSource = !!authStore.user?.id
      for (const raw of rows) {
        // Источник — из manager_id ответа (этап 0); нет поля — старый бэк, берём сегмент.
        // Чужой аккаунт в ответе — дефект бэка: такое сообщение в сегмент не кладём.
        const source =
          raw.managerId && canCheckSource
            ? toSourceManagerId(raw.managerId)
            : segment.sourceManagerId
        if (source !== segment.sourceManagerId) {
          foreignRows += 1
          continue
        }
        page.push(toFeedMessage(segment, mapTelegramMessageToMessageInfo(raw)))
      }
      page.reverse()
      if (foreignRows > 0) {
        console.warn(
          `[telegram] getMessages: dropped ${foreignRows} message(s) of another account`,
          { chatKey: segment.chatKey },
        )
      }
      if (mode === 'next') {
        // Offset-пагинация: WS-аппенды сдвигают страницы — повторы отбрасываем.
        const known = new Set(segment.messages.map((m) => m.id))
        segment.messages = [...page.filter((m) => !known.has(m.id)), ...segment.messages]
      } else {
        segment.messages = [...page, ...keepUnsent(segment, page)]
      }
      // offset — по строкам сервера, отброшенные тоже занимают позиции. Страница без
      // единой принятой строки исчерпывает сегмент — иначе цикл догрузки бесконечен.
      segment.offset = offset + rows.length
      segment.total = Number(response.pagination?.total) || 0
      segment.hasMore = page.length > 0 && segment.offset < segment.total
      segment.chatInfo = response.chatInfo ? mapTelegramChatToChatInfo(response.chatInfo) : null
      return true
    } catch (e) {
      error.value = reportTelegramError('getMessages', e)
      return false
    } finally {
      segment.isLoading = false
    }
  }

  // Открыть один чат без загрузки (единственный сегмент). managerId (DEV-998):
  // uuid чужого менеджера (read-only) или 'support'; null/undefined = свой чат.
  function openChat(peerKey: string, managerId?: string | null): ChatSegment {
    openGeneration += 1
    segments.value.clear()
    isMergedView.value = false
    mergedReady.value = false
    viewingManagerId.value = managerId ?? null
    currentPeerKey.value = peerKey
    return openSegment(peerKey, currentSourceId.value)
  }

  async function setCurrentChat(peerKey: string, managerId?: string | null) {
    const segment = openChat(peerKey, managerId)
    await loadSegmentPage(segment, 'first')
  }

  // Объединённая лента клиента (DEV-1835): по сегменту на каждый чат из
  // getClientChats (support, менеджеры, свой), первые страницы — параллельно.
  // «Текущий» чат — support (в него уходит отправка), без него — первый в списке.
  async function openMergedView(clientId: string) {
    const generation = ++openGeneration
    await loadClientChats(clientId)
    if (generation !== openGeneration) return

    segments.value.clear()
    isMergedView.value = true
    mergedReady.value = false
    const opened: ChatSegment[] = []
    for (const chat of clientChats.value) {
      if (!chat.managerId) continue
      const sourceManagerId = toSourceManagerId(chat.managerId)
      if (segments.value.has(buildChatKey(sourceManagerId, chat.peerKey))) continue
      opened.push(openSegment(chat.peerKey, sourceManagerId))
    }
    const primary =
      opened.find((segment) => segment.sourceManagerId === SUPPORT_MANAGER_ID) ?? opened[0]
    viewingManagerId.value = primary ? (toTargetManagerId(primary.sourceManagerId) ?? null) : null
    currentPeerKey.value = primary?.peerKey ?? null

    await Promise.allSettled(opened.map((segment) => loadSegmentPage(segment, 'first')))
    if (generation !== openGeneration) return
    await revealHiddenSources(generation)
    if (generation === openGeneration) mergedReady.value = true
  }

  // DEV-2227: до показа ленты догружаем источник на границе, пока какой-то источник спрятан
  // целиком. Не больше MERGED_REVEAL_PAGES страниц — дальше подсказка сверху и прокрутка.
  const MERGED_REVEAL_PAGES = 5

  async function revealHiddenSources(generation: number) {
    for (let page = 0; page < MERGED_REVEAL_PAGES && hiddenMergedSources.value.length; page++) {
      const next = mergedSegmentToLoad()
      if (!next) return
      const loaded = await loadSegmentPage(next.segment, next.mode)
      if (!loaded || generation !== openGeneration) return
    }
  }

  function resetChat() {
    openGeneration += 1
    segments.value.clear()
    isMergedView.value = false
    mergedReady.value = false
    currentPeerKey.value = null
    viewingManagerId.value = null
    typingPeerKey.value = null
  }

  // Досинхронизация открытых чатов (напр. после переподключения WS): события,
  // потерянные в разрыв (статусы прочтения, новые сообщения), догружаются из БД.
  function reloadCurrentChat() {
    for (const segment of segments.value.values()) void loadSegmentPage(segment, 'first')
  }

  // DEV-2561: привязка докачивает в mp выброшенные сообщения диалога. Перечитываем первую
  // страницу, только если открыт один свой чат с этим пиром; pending и failed держит keepUnsent.
  async function reloadOwnChat(peerKey: string) {
    if (isMergedView.value || currentSourceId.value !== OWN_SOURCE_ID) return
    if (currentPeerKey.value !== peerKey) return
    const segment = currentSegment.value
    if (segment) await loadSegmentPage(segment, 'first')
  }

  // Следующая страница истории: текущего сегмента или, в объединённой ленте,
  // сегментов, держащих границу отображения.
  async function loadMore() {
    if (!isMergedView.value) {
      const segment = currentSegment.value
      if (!segment || segment.isLoading || !segment.hasMore) return
      await loadSegmentPage(segment, 'next')
      return
    }
    // Грузим, пока граница не сдвинется: при равной секунде у двух сегментов одна
    // страница видимую часть не растит, а IntersectionObserver повторно не сработает.
    // Ошибка прерывает цикл (следующий скролл повторит); предел итераций — по
    // странице на каждый открытый сегмент — страхует от дефектного бэка.
    const boundaryBefore = mergedBoundary.value
    for (let step = 0; step < segments.value.size; step++) {
      const next = mergedSegmentToLoad()
      if (!next) return
      const loaded = await loadSegmentPage(next.segment, next.mode)
      if (!loaded || mergedBoundary.value !== boundaryBefore) return
    }
  }

  // ===================== send =====================

  // DEV-2109: провал отправки раньше только убирал пузырь — человек не узнавал, что
  // сообщение не ушло. Код для тоста: gateway кладёт gRPC-код в тело ответа, 403
  // не сюда — его тостом «Права изменились» уже показывает интерцептор.
  const sendFailure = ref<TelegramSendFailure | null>(null)

  function classifySendError(e: unknown): TelegramSendFailure | null {
    const response = (
      e as { response?: { status?: number; data?: { code?: number; message?: string } } } | null
    )?.response
    if (response?.status === 403) return null
    const code = response?.data?.code
    const message = response?.data?.message ?? ''
    const at = Date.now()
    if (code === GRPC_RESOURCE_EXHAUSTED || response?.status === 429) {
      const seconds = Number(/retry after (\d+)/.exec(message)?.[1]) || 0
      return { code: 'flood', seconds, at }
    }
    if (code === GRPC_FAILED_PRECONDITION) {
      if (message.includes('not connected')) return { code: 'not_connected', at }
      if (message.includes('unknown')) return { code: 'unknown_peer', at }
    }
    return { code: 'generic', at }
  }

  function pushOptimistic(segment: ChatSegment, content: string, mediaType: MediaType): string {
    const now = new Date().toISOString()
    const id = newPendingId()
    segment.messages = [
      ...segment.messages,
      toFeedMessage(segment, {
        id,
        chatJid: segment.peerKey,
        senderJid: 'me',
        content,
        timestamp: now,
        isFromMe: true,
        mediaType,
        status: 'pending',
        createdAt: now,
        updatedAt: now,
      }),
    ]
    return id
  }

  function dropOptimistic(segment: ChatSegment, id: string) {
    segment.messages = segment.messages.filter((m) => m.id !== id)
  }

  // DEV-2492. Неотправленный текст остаётся в ленте «Не отправлено» с «Повторить».
  // Префикс failed-: WS-подтверждения ищут pending- и этот пузырь не тронут.
  function failOptimistic(segment: ChatSegment, id: string) {
    segment.messages = segment.messages.map((m) =>
      m.id === id ? toFeedMessage(segment, { ...m, id: toFailedId(m.id), status: 'failed' }) : m,
    )
  }

  // DEV-2492. «Повторить» убирает неотправленный пузырь — текст уйдёт новой отправкой.
  function discardMessage(id: string) {
    for (const segment of segments.value.values()) {
      if (segment.messages.some((m) => m.id === id)) {
        segment.messages = segment.messages.filter((m) => m.id !== id)
      }
    }
  }

  // DEV-2492. Досинхрон (первая страница, в том числе после переподключения WS)
  // не стирает ни неотправленный текст, ни летящую попытку («Повторить» жмут как
  // раз после возвращения сети). Локальный пузырь уходит, только если на странице
  // появилось моё новое исходящее с тем же текстом в окне попытки — одно на один
  // пузырь: уже известное ленте прежнее «Ок» второе, неотправленное, не стирает.
  function keepUnsent(segment: ChatSegment, page: TelegramFeedMessage[]): TelegramFeedMessage[] {
    const me = useAuthStore().user?.id
    const known = new Set(segment.messages.map((m) => m.id))
    const fresh = page.filter((m) => !known.has(m.id) && isOwnOutgoing(m, me))
    return segment.messages.filter((local) => {
      if (!isLocalMessage(local)) return false
      const idx = deliveryIndex(local, fresh)
      if (idx === -1) return true
      fresh.splice(idx, 1)
      return false
    })
  }

  // pending → отправленное: id/статус/время от сервера, uid пересчитан под новый id.
  function promotePending(segment: ChatSegment, idx: number, id: string, timestamp: string) {
    const pending = segment.messages[idx]
    if (!pending) return
    segment.messages = [
      ...segment.messages.slice(0, idx),
      toFeedMessage(segment, { ...pending, id, status: 'sent', timestamp }),
      ...segment.messages.slice(idx + 1),
    ]
  }

  // DEV-1833: резолв optimistic из REST-ответа — не зависим от доставки WS
  // (лаг, разрыв, фронт раскатан раньше бэка). Если WS успел раньше — no-op.
  function resolveOptimistic(
    segment: ChatSegment,
    optimisticId: string,
    response: TelegramSendMessageResponse,
  ) {
    const idx = segment.messages.findIndex((m) => m.id === optimisticId)
    const pending = segment.messages[idx]
    if (idx === -1 || !pending || !response.messageId) return
    promotePending(segment, idx, response.messageId, response.timestamp || pending.timestamp)
  }

  // target_manager_id запроса по источнику сегмента ('support'; свой — без поля).
  function withTarget<T extends { target_manager_id?: string }>(req: T, segment: ChatSegment): T {
    const target = toTargetManagerId(segment.sourceManagerId)
    if (target) req.target_manager_id = target
    return req
  }

  // Сегмент для записи по peerKey в текущем виде; чужой менеджер — нет (DEV-998).
  // В объединённой ленте пишем только от поддержки (от имени менеджера — DEV-1674).
  function writableSegment(peerKey: string): ChatSegment | null {
    const source = currentSourceId.value
    if (!isWritableSource(source)) return null
    if (isMergedView.value && source !== SUPPORT_MANAGER_ID) return null
    return segments.value.get(buildChatKey(source, peerKey)) ?? null
  }

  async function sendText(peerKey: string, text: string, clientId?: string, replyTo?: string) {
    const segment = writableSegment(peerKey)
    if (!segment) return
    const optimisticId = pushOptimistic(segment, text, 'text')
    try {
      const req: TelegramSendTextRequest = { peer_key: peerKey, message: text }
      if (clientId) req.client_id = clientId
      if (replyTo) req.reply_to_msg_id = replyTo
      const response = await telegramApi.sendText(withTarget(req, segment))
      resolveOptimistic(segment, optimisticId, response)
    } catch (e) {
      failOptimistic(segment, optimisticId)
      error.value = reportTelegramError('sendText', e)
      sendFailure.value = classifySendError(e)
    }
  }

  async function sendImage(peerKey: string, image: string, caption?: string, clientId?: string) {
    const segment = writableSegment(peerKey)
    if (!segment) return
    const optimisticId = pushOptimistic(segment, caption || '', 'image')
    try {
      const req: TelegramSendImageRequest = { peer_key: peerKey, image }
      if (caption) req.caption = caption
      if (clientId) req.client_id = clientId
      const response = await telegramApi.sendImage(withTarget(req, segment))
      resolveOptimistic(segment, optimisticId, response)
    } catch (e) {
      dropOptimistic(segment, optimisticId)
      error.value = reportTelegramError('sendImage', e)
      sendFailure.value = classifySendError(e)
    }
  }

  async function sendDocument(
    peerKey: string,
    document: string,
    fileName?: string,
    clientId?: string,
  ) {
    const segment = writableSegment(peerKey)
    if (!segment) return
    const optimisticId = pushOptimistic(segment, fileName || '', 'document')
    try {
      const req: TelegramSendDocumentRequest = { peer_key: peerKey, document }
      if (fileName) req.file_name = fileName
      if (clientId) req.client_id = clientId
      const response = await telegramApi.sendDocument(withTarget(req, segment))
      resolveOptimistic(segment, optimisticId, response)
    } catch (e) {
      dropOptimistic(segment, optimisticId)
      error.value = reportTelegramError('sendDocument', e)
      sendFailure.value = classifySendError(e)
    }
  }

  // sourceManagerId (DEV-1835): источник сообщения в объединённой ленте; по
  // умолчанию — текущий вид. Чужие менеджерские сегменты не помечаем (DEV-998).
  async function markAsRead(peerKey: string, messageId: string, sourceManagerId?: string) {
    const source = sourceManagerId ?? currentSourceId.value
    if (!isWritableSource(source)) return
    try {
      const req: TelegramMarkAsReadRequest = { peer_key: peerKey, message_id: messageId }
      const target = toTargetManagerId(source)
      if (target) req.target_manager_id = target // DEV-1833: 'support'
      await telegramApi.markAsRead(req)
    } catch (e) {
      error.value = reportTelegramError('markAsRead', e)
    }
  }

  async function react(
    peerKey: string,
    messageId: string,
    emoji: string,
    sourceManagerId?: string,
  ) {
    const source = sourceManagerId ?? currentSourceId.value
    if (!isWritableSource(source)) return // DEV-998: read-only чужого чата — без реакций
    // оптимистично отражаем свою реакцию
    const segment = segments.value.get(buildChatKey(source, peerKey))
    if (segment) applyReaction(segment, messageId, emoji, 'me', true)
    try {
      const req: TelegramReactRequest = { peer_key: peerKey, message_id: messageId, emoji }
      const target = toTargetManagerId(source)
      if (target) req.target_manager_id = target // DEV-1833
      await telegramApi.react(req)
    } catch (e) {
      error.value = reportTelegramError('react', e)
    }
  }

  // silent (DEV-2225): карточка привязывает найденный свой чат сама. Сервер сверяет чат с
  // клиентом и вправе отказать — человеку это не ошибка, чат открыт как раньше.
  async function bindChat(
    peerKey: string,
    clientId: string,
    options: { silent?: boolean } = {},
  ): Promise<boolean> {
    if (viewingManagerId.value) return false // DEV-998: read-only чужого чата — не перепривязываем
    if (!options.silent) error.value = null
    try {
      await telegramApi.bindChatToClient({ peer_key: peerKey, client_id: clientId })
      const chat = chats.value.find((c) => c.peerKey === peerKey)
      if (chat) chat.clientId = clientId
      return true
    } catch (e) {
      if (options.silent) return false
      error.value = reportTelegramError('bindChat', e)
      throw e
    }
  }

  // ===================== WebSocket handlers =====================
  // Событие уходит в сегмент (account_id ?? myId, peer_key); нет такого открытого
  // сегмента — игнор (DEV-1833/DEV-1835). peer_key один у всех аккаунтов клиента,
  // а tg_msg_id уникален только внутри аккаунта — поиск всегда внутри сегмента.

  // DEV-2227: входящее поддержки по диалогу, которого нет среди чатов клиента. Чей он,
  // событие не говорит (автолид привяжет чат позже), — карточка перечитает свои чаты.
  const unknownSupportChat = ref<{ peerKey: string } | null>(null)

  function noteUnknownSupportChat(payload: WsTelegramNewMessagePayload) {
    if (payload.account_id !== SUPPORT_MANAGER_ID || payload.is_from_me) return
    const known = clientChats.value.some(
      (chat) => chat.managerId === SUPPORT_MANAGER_ID && chat.peerKey === payload.peer_key,
    )
    if (!known) unknownSupportChat.value = { peerKey: payload.peer_key }
  }

  function addMessageFromWs(payload: WsTelegramNewMessagePayload) {
    const segment = segmentForWs(payload.peer_key, payload.account_id)
    if (!segment) {
      noteUnknownSupportChat(payload)
      return
    }
    if (segment.messages.some((m) => m.id === payload.id)) return
    segment.messages = [
      ...segment.messages,
      toFeedMessage(segment, {
        id: payload.id,
        chatJid: payload.peer_key,
        senderJid: payload.sender_tg_id,
        content: payload.content,
        timestamp: payload.timestamp,
        isFromMe: payload.is_from_me,
        mediaType: mapTelegramMediaType(payload.media_type),
        status: 'delivered',
        createdAt: payload.timestamp,
        updatedAt: payload.timestamp,
        // DEV-1676: эхо ответа бота раньше MESSAGE_SENT — метка «AI» только отсюда.
        authorType: payload.author_type,
        authorManagerId: payload.author_manager_id,
      }),
    ]
    // входящее сообщение снимает индикатор набора
    if (payload.peer_key === typingPeerKey.value) typingPeerKey.value = null
  }

  function handleMessageSent(payload: WsTelegramMessageSentPayload) {
    const segment = segmentForWs(payload.peer_key, payload.account_id)
    if (!segment) return
    // DEV-1676: ответ AI-ассистента — не моя попытка, даже с тем же текстом.
    const bot = payload.author_type === BOT_AUTHOR_TYPE
    const idx = bot
      ? -1
      : segment.messages.findIndex(
          (m) => m.id.startsWith('pending-') && m.content === payload.content,
        )
    if (idx !== -1) {
      promotePending(segment, idx, payload.id, payload.timestamp)
      return
    }
    // DEV-2492. Запрос упал, а раннер сообщение дослал: эхо подтверждает «Не
    // отправлено». В support-чате пишут несколько сотрудников — там только своё эхо.
    // DEV-1676: AI-ассистент пишет и в личный Telegram менеджера из его сессии — его
    // ответ не моё эхо ни в одном сегменте.
    const ownEcho =
      !bot &&
      (segment.sourceManagerId !== SUPPORT_MANAGER_ID ||
        payload.author_manager_id === useAuthStore().user?.id)
    if (ownEcho) {
      const unsentIdx = findDeliveredUnsent(segment.messages, payload.content, payload.timestamp)
      if (unsentIdx !== -1) {
        promotePending(segment, unsentIdx, payload.id, payload.timestamp)
        return
      }
    }
    // DEV-1833: исходящее коллеги (второй admin в том же support-чате) приходит
    // ТОЛЬКО этим событием — аппендим. Свои уже зарезолвлены REST-ответом → dedup.
    if (segment.messages.some((m) => m.id === payload.id)) return
    segment.messages = [
      ...segment.messages,
      toFeedMessage(segment, {
        id: payload.id,
        chatJid: payload.peer_key,
        senderJid: 'me',
        content: payload.content,
        timestamp: payload.timestamp,
        isFromMe: true,
        mediaType: mapTelegramMediaType(payload.media_type),
        status: 'sent',
        createdAt: payload.timestamp,
        updatedAt: payload.timestamp,
        // DEV-1676: автор — для подписи «AI-ассистент» у ответа бота.
        authorType: payload.author_type,
        authorManagerId: payload.author_manager_id,
      }),
    ]
  }

  function updateMessageStatus(payload: WsTelegramMessageStatusPayload) {
    const segment = segmentForWs(payload.peer_key, payload.account_id)
    if (!segment) return
    // 'read' — водяная метка: TELEGRAM_MESSAGE_STATUS.id = MaxID (макс. tg_msg_id,
    // прочитанный собеседником). Telegram шлёт прочтение границей — помечаем ВСЕ свои
    // сообщения сегмента с tg_msg_id <= MaxID (точное совпадение почти всегда мимо). DEV-979.
    if (payload.status === 'read') {
      const maxId = Number(payload.id)
      if (!Number.isFinite(maxId)) return
      for (const m of segment.messages) {
        if (m.isFromMe && m.status !== 'read' && Number(m.id) <= maxId) {
          m.status = 'read'
        }
      }
      return
    }
    const msg = segment.messages.find((m) => m.id === payload.id)
    if (msg) msg.status = payload.status as MessageStatus
  }

  function updateMessageMedia(payload: WsTelegramMediaUpdatePayload) {
    const segment = segmentForWs(payload.peer_key, payload.account_id)
    const msg = segment?.messages.find((m) => m.id === payload.id)
    if (!msg) return
    msg.mediaType = mapTelegramMediaType(payload.media_type)
    if (payload.url) msg.url = payload.url
    if (payload.filename) msg.filename = payload.filename
    if (payload.file_length) msg.fileLength = payload.file_length
  }

  function applyReaction(
    segment: ChatSegment,
    messageId: string,
    emoji: string,
    senderJid: string,
    isFromMe: boolean,
  ) {
    const msg = segment.messages.find((m) => m.id === messageId)
    if (!msg) return
    const reactions = (msg.reactions ?? []).filter((r) => r.senderJid !== senderJid)
    if (emoji) {
      reactions.push({ emoji, senderJid, isFromMe, timestamp: new Date().toISOString() })
    }
    msg.reactions = reactions
  }

  function updateMessageReaction(payload: WsTelegramReactionPayload) {
    const segment = segmentForWs(payload.peer_key, payload.account_id)
    if (!segment) return
    applyReaction(
      segment,
      payload.target_message_id,
      payload.emoji,
      payload.sender_tg_id,
      payload.is_from_me,
    )
  }

  function handleEdited(payload: WsTelegramEditedPayload) {
    const segment = segmentForWs(payload.peer_key, payload.account_id)
    const msg = segment?.messages.find((m) => m.id === payload.id)
    if (!msg) return
    msg.content = payload.content
    msg.updatedAt = payload.edited_at
  }

  function handleDeleted(payload: WsTelegramDeletedPayload) {
    const segment = segmentForWs(payload.peer_key, payload.account_id)
    if (!segment) return
    segment.messages = segment.messages.filter((m) => m.id !== payload.id)
  }

  function setTyping(payload: WsTelegramTypingPayload) {
    if (!segmentForWs(payload.peer_key, payload.account_id)) return
    typingPeerKey.value = payload.peer_key
    if (typingTimer) clearTimeout(typingTimer)
    typingTimer = setTimeout(() => {
      if (typingPeerKey.value === payload.peer_key) typingPeerKey.value = null
    }, 4000)
  }

  // DEV-2227: сбрасываем только свой чат — поддержку, чужой чат и объединённую ленту
  // читаем и без личной сессии, раньше они пропадали до F5.
  function dropOwnChatIfOffline(next: TelegramAccountStatus) {
    const ownChatOpen =
      currentPeerKey.value !== null && !isMergedView.value && viewingManagerId.value === null
    if ((next === 'revoked' || next === 'inactive') && ownChatOpen) resetChat()
  }

  function handleAccountStatus(payload: WsTelegramAccountStatusPayload) {
    // Статус пришёл от сервера — состоянием владеет сессия (DEV-1712).
    applyAccountStatus(payload.status)
    if (account.value) account.value.status = payload.status
    dropOwnChatIfOffline(payload.status)
  }

  // Снимок PROVIDER_STATUS: после переподключения сокета отзыв, пропущенный в разрыве, не теряется.
  function handleAccountSnapshot(snapshot: WsTelegramAccountSnapshot) {
    applyAccountSnapshot(snapshot)
    dropOwnChatIfOffline(snapshot.status)
  }

  return {
    // session
    status,
    account,
    isLoadingStatus,
    isStatusKnown,
    isConnected,
    loadStatus,
    logout,
    isSavingCallsCrmOnly,
    failedCallsCrmOnly,
    setCallsCrmOnly,
    // login flow
    loginState,
    loginId,
    qrText,
    qrImageBase64,
    qrExpiresAt,
    loginError,
    isSubmittingPassword,
    // Личный вход шаг 2FA без нового QR предложить не может — login_id попытки потерян.
    canResumePasswordEntry,
    startLogin,
    submitPassword,
    resumePasswordEntry,
    resetLogin,
    // chats
    chats,
    chatsTotal,
    isLoadingChats,
    loadChats,
    // cross-manager view (DEV-998)
    clientChats,
    loadClientChats,
    viewingManagerId,
    // open chats (DEV-1835)
    segments, // наружу только для тестов; UI ходит через messages/isMergedView
    openChat,
    isMergedView,
    openMergedView,
    readReceiptSources,
    hiddenMergedSources,
    // messages
    currentPeerKey,
    // DEV-2502: источник открытого чата — часть ключа черновика (свой и поддержка делят peerKey).
    currentSourceId,
    currentChatInfo,
    messages,
    hasMoreMessages,
    isLoadingMessages,
    typingPeerKey,
    setCurrentChat,
    resetChat,
    reloadCurrentChat,
    reloadOwnChat,
    loadMore,
    // send / actions
    sendText,
    discardMessage,
    sendImage,
    sendDocument,
    // DEV-2109: последний провал отправки — карточка показывает тост
    sendFailure,
    markAsRead,
    react,
    bindChat,
    // WS
    addMessageFromWs,
    handleMessageSent,
    updateMessageStatus,
    updateMessageMedia,
    updateMessageReaction,
    handleEdited,
    handleDeleted,
    setTyping,
    handleQrUpdate,
    handleLoginStatus,
    handleAccountStatus,
    handleAccountSnapshot,
    unknownSupportChat,
    // errors
    error,
  }
})
